/* ============================================================
   NR MINI PRACTICE BUNDLE — campaign landing page behaviour

   Built to Figma "mini-pro-landing-page-mobile" (node 618:5), which has
   direct actions rather than a chooser: every button either adds the net or
   adds the three-piece bundle.

   The bundle add sends the three approved variants in one /cart/add.js
   request with the same private line-item properties the Mini PDP uses, so
   the cart drawer, cart page and discount function treat a landing-page
   bundle identically to a PDP bundle:

       _bundle_offer    = mini-starter-69999
       _bundle_instance = one shared value across the three lines
       _bundle_role     = net | barriers | mat

   The server-side Shopify Discount is the pricing authority. Nothing here
   sets or asserts a price: the page states the promotional price up front
   because both automatic discounts are live and verified, and this file
   still confirms the exact allocation against /cart.js after the add.

   A GA4 promotion add event is emitted only after that confirmation.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-nr-mpb]');
  if (!root || root.dataset.offerLive !== '1') return;

  var addButtons = Array.prototype.slice.call(root.querySelectorAll('[data-mpb-add]'));
  if (!addButtons.length) return;

  var offerId = root.dataset.offerId || 'mini-starter-69999';
  var offerName = root.dataset.offerName || 'Mini Practice Bundle';
  var cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
  var cartUrl = root.dataset.cartUrl || '/cart';
  var cartType = root.dataset.cartType || 'drawer';
  var genericError = root.dataset.genericError || 'Something went wrong. Please try again.';
  var networkError = root.dataset.networkError || 'Connection problem. Please try again.';

  var netVid = parseInt(root.dataset.netVid, 10);
  var barriersVid = parseInt(root.dataset.barriersVid, 10);
  var matVid = parseInt(root.dataset.matVid, 10);

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function promotionEvent(eventName) {
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: { items: [{ promotion_id: offerId, promotion_name: offerName }] }
    });
  }

  function isVerifiedAutomaticAllocation(allocation, exactTitle) {
    var application = allocation && allocation.discount_application ? allocation.discount_application : {};
    var title = String(application.title || '');
    /* /cart.js leaves discount_application.type blank for native automatic discounts on
       this store (verified against a live $699.99 bundle cart 2026-08-11), so a blank
       type counts as automatic. Shopper-supplied kinds still fail this check. */
    var type = String(application.type || '').toLowerCase();
    var isAutomatic = type === 'automatic' || type === '';
    return Number(allocation && allocation.amount) > 0 && isAutomatic && title === exactTitle;
  }

  function verifiedBundleDiscount(cart) {
    var expected = { net: 40942235517021, barriers: 40456698560605, mat: 39412528119901 };
    var groups = {};

    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var properties = item.properties || {};
      var instance = properties._bundle_instance;
      var role = properties._bundle_role;
      if (properties._bundle_offer !== offerId || !instance || expected[role] !== Number(item.variant_id) || Number(item.quantity) !== 1) return;
      if (!groups[instance]) groups[instance] = {};
      groups[instance][role] = item;
    });

    var instances = Object.keys(groups);
    for (var i = 0; i < instances.length; i += 1) {
      var group = groups[instances[i]];
      var roles = ['net', 'barriers', 'mat'];
      if (!roles.every(function (role) { return !!group[role]; })) continue;

      var finalTotal = roles.reduce(function (sum, role) { return sum + Number(group[role].final_line_price || 0); }, 0);
      var discountTotal = 0;
      var allVerified = roles.every(function (role) {
        var verified = (group[role].line_level_discount_allocations || []).filter(function (allocation) {
          return isVerifiedAutomaticAllocation(allocation, 'Mini Practice Bundle — Save $127.01');
        });
        verified.forEach(function (allocation) { discountTotal += Number(allocation.amount || 0); });
        return verified.length > 0;
      });

      if (allVerified && discountTotal === 12701 && finalTotal === 69999) {
        return { confirmed: true, discountTotal: discountTotal };
      }
    }

    return { confirmed: false, discountTotal: 0 };
  }

  function verifiedMiniNetDiscount(cart) {
    var item = (cart && cart.items ? cart.items : []).find(function (candidate) {
      var properties = candidate.properties || {};
      return Number(candidate.variant_id) === 40942235517021 && Number(candidate.quantity) === 1 && properties._mini_offer === 'mini-net-499';
    });
    if (!item || Number(item.final_line_price) !== 49900) return { confirmed: false, discountTotal: 0 };

    var verified = (item.line_level_discount_allocations || []).filter(function (allocation) {
      return isVerifiedAutomaticAllocation(allocation, 'Mini Net — Save $50.00');
    });
    var discountTotal = verified.reduce(function (sum, allocation) { return sum + Number(allocation.amount || 0); }, 0);
    return { confirmed: verified.length > 0 && discountTotal > 0, discountTotal: discountTotal };
  }

  function fetchCart() {
    return fetch(cartUrl + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (response) {
      if (!response.ok) throw new Error('Cart refresh failed');
      return response.json();
    });
  }

  /* Each purchase group owns the status region nearest it, so the message
     lands beside the control the shopper actually pressed. */
  function statusFor(button) {
    var scope = button.closest('.nr-mini__pricecard, .nr-mini__offerbox, .nr-mini__closeactions') || root;
    return scope.querySelector('[data-mpb-status]');
  }

  function setStatus(button, message, isError) {
    var el = statusFor(button);
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('is-error', !!isError);
  }

  function setBusy(button, busy) {
    var original = button.dataset.originalLabel || button.textContent.trim();
    button.dataset.originalLabel = original;
    addButtons.forEach(function (control) {
      if (busy) control.setAttribute('disabled', 'disabled');
      else control.removeAttribute('disabled');
    });
    button.textContent = busy ? 'Adding…' : original;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function announceCart(cart, sections) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (cart) {
      cart.sections = sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:build', { bubbles: true, detail: { cart: cart, openMiniCart: cartType === 'drawer' } })
      );
    }

    if (cartType !== 'drawer') {
      window.location.href = cartUrl;
      return;
    }

    window.setTimeout(function () {
      var drawer = document.getElementById('mini-cart');
      if (drawer && !drawer.hasAttribute('open') && typeof drawer.open !== 'undefined') {
        drawer.open = true;
      }
    }, 400);
  }

  function buildItems(kind) {
    if (kind === 'bundle') {
      if (!netVid || !barriersVid || !matVid) return null;
      var instance = offerId + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var ids = [netVid, barriersVid, matVid];
      var roles = ['net', 'barriers', 'mat'];
      return ids.map(function (id, index) {
        return {
          id: id,
          quantity: 1,
          properties: { _bundle_offer: offerId, _bundle_instance: instance, _bundle_role: roles[index] }
        };
      });
    }
    if (!netVid) return null;
    return [{ id: netVid, quantity: 1, properties: { _mini_offer: 'mini-net-499' } }];
  }

  function addToCart(button) {
    var kind = button.dataset.mpbAdd === 'bundle' ? 'bundle' : 'net';
    var isBundle = kind === 'bundle';
    var items = buildItems(kind);

    if (!items) {
      setStatus(button, genericError, true);
      return;
    }

    setBusy(button, true);
    setStatus(button, '');

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items, sections: 'mini-cart' })
    })
      .then(function (response) {
        return response.json().then(function (json) { return { ok: response.ok, json: json }; });
      })
      .then(function (result) {
        setBusy(button, false);
        if (!result.ok) {
          setStatus(button, (result.json && result.json.description) || genericError, true);
          return;
        }
        fetchCart()
          .then(function (cart) {
            var verified = isBundle ? verifiedBundleDiscount(cart) : verifiedMiniNetDiscount(cart);
            if (verified.confirmed) {
              setStatus(
                button,
                (isBundle ? 'Mini Practice Bundle' : 'Mini net') +
                  ' discount confirmed in cart: -' + money(verified.discountTotal) + '.'
              );
            } else {
              setStatus(
                button,
                isBundle
                  ? 'All 3 items added at current prices. No Mini Practice Bundle discount is applied.'
                  : 'Mini net added at the current price. No Mini net discount is applied.'
              );
            }
            if (isBundle && verified.confirmed) promotionEvent('add_to_cart');
            announceCart(cart, result.json.sections);
          })
          .catch(function () {
            setStatus(button, 'Items added. Open cart to confirm current pricing and any discount.');
            announceCart(null, result.json.sections);
          });
      })
      .catch(function () {
        setBusy(button, false);
        setStatus(button, networkError, true);
      });
  }

  addButtons.forEach(function (button) {
    button.addEventListener('click', function () { addToCart(button); });
  });
})();
